import * as vp from 'vopay';
import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useStorage from '../hooks/useStorage';
import useFetchPaylinkSingleUseToken from '../hooks/useApi/useFetchPaylinkSingleUseToken';
import useFetchAccountVerification from '../hooks/useApi/useFetchAccountVerification';
import useFetchAccountBranding from '../hooks/useApi/useFetchAccountBranding';

type AccountVerificationCtx = {
  isVerification: boolean;
  isLoaded: boolean;
  verificationInfo: vp.ELinxSingleUseTokenResponse;
  verificationRequests: vp.AccountVerificationResponse;
  brandingInfo: vp.AccountBrandingResponse;
  verificationRequestsReset: () => void;
};

export const AccountVerificationContext = createContext<AccountVerificationCtx>({
  isVerification: false,
  isLoaded: false,
  verificationInfo: {
    JWT: '',
    Success: false,
    ErrorMessage: '',
    Token: '',
    IsValidToken: false,
    ExtraInfo: {
      PayLinkDetails: {
        FirstName: '',
        LastName: '',
        ReceiverName: '',
        SenderEmailAddress: '',
        ReceiverEmailAddress: '',
        InvoiceNumber: '',
        Note: '',
        SenderName: '',
        Amount: '',
        Currency: '',
        Status: '',
        FailureReason: '',
        Link: '',
        IframeURL: '',
        IframeKey: '',
        TransactionID: '',
        ScheduledTransactionID: '',
        EFTType: '',
        PaymentType: '',
        TransactionDate: '',
        Frequency: '',
        NameOfFrequency: '',
        SemiMonthlyFrequencyType: '',
        ScheduleEndDate: '',
        EndingAfterPayments: '',
        Type: '',
        CanDecline: '',
        ScheduleStartDate: '',
        ExpireDate: 0,
        LastModified: '',
        BankAccountLinked: false,
        BankAccountToken: '',
        RecipientMessage: '',
        FlexiblePaymentOptions: '',
        MinimumPaymentAccepted: '',
        RemainderPaymentDate: '',
        ReceiverPhoneNumber: '',
        PaymentMethodDisplayOptions: 'New',
        ClientAccountID: '',
        DisplayEmbbedInterac: false,
        BankAccountRail: ['eft'],
        DocumentLink: '',
      },
      VerificationDetails: {
        VerificationRequestID: '',
        Amount: '',
        SenderEmailAddress: '',
        ReceiverEmailAddress: '',
        Link: '',
        VerificationStatus: '',
        VerificationAttempts: '',
        VerificationStartDate: '',
        VerificationAttemptDate: '',
        ExpireDate: '',
      },
    },
    Name: '',
    LegalBusinessName: '',
    BusinessAddress: {
      Address1: '',
      Address2: '',
      City: '',
      Province: '',
      Country: '',
    },
    ContactAddress: {
      Address1: '',
      Address2: '',
      City: '',
      Province: '',
      PostalCode: '',
      Country: '',
    },
    IsPartnerToken: false,
    AccountStatus: '',
    PartnerAccountType: '',
    SubAccountType: '',
  },
  verificationRequests: {
    Success: false,
    ErrorMessage: '',
    VerificationRequests: {
      0: {
        VerificationRequestID: '',
        VerificationType: 'bank',
        Amount: '',
        Details: {
          Credit: {
            FirstName: '',
            LastName: '',
          },
          Debit: {
            FirstName: '',
            LastName: '',
          },
          Bank: {
            AccountNumber: '',
            FinancialInstitutionId: '',
          },
          EmailAddress: {
            AccountHolderName: '',
            EmailAddress: '',
          },
        },
        Status: {
          Transaction: {
            Credit: '',
            Debit: '',
          },
          Verification: '',
          Request: '',
        },
        VerificationAttempts: '',
        EmailAddress: {
          Sender: '',
          Receiver: '',
        },
        Link: '',
        LastModified: '',
      },
    },
  },
  brandingInfo: {
    Success: false,
    ErrorMessage: '',
    BrandingDetails: {
      PrimaryColor: '#FFD147',
      SecondaryColor: '',
    },
    Logo: '',
    SmallLogo: '',
    Favicon: '',
    AppTitle: '',
  },
  verificationRequestsReset: () => {},
});

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

export const AccountVerificationProvider = ({ children }: { children: ReactNode }): React.JSX.Element => {
  const history = useHistory();
  const params = useQuery();
  const storage = useStorage();
  const [isVerification, setIsVerification] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const { accountVerificationToken } = storage.accountVerificationToken.getAccountVerificationToken();
  const [verificationInfo, fetchSingleUseTokenInfo] = useFetchPaylinkSingleUseToken({});
  const [brandingInfo, fetchAccountBranding] = useFetchAccountBranding({});
  const [verificationRequests, fetchAccountVerification] = useFetchAccountVerification({});

  useEffect(() => {
    if (params.get('AccountVerificationToken')) {
      storage.accountVerificationToken.storeAccountVerificationToken(params.get('AccountVerificationToken') || '');
    }
  }, [params]);

  useEffect(() => {
    if (accountVerificationToken !== '') {
      fetchSingleUseTokenInfo({
        Token: accountVerificationToken,
        ApplicationType: 'verification',
      });
      setIsVerification(true);
    }
  }, [accountVerificationToken]);
  useEffect(() => {
    if (accountVerificationToken && isVerification) {
      setIsLoaded(true);
    }
  }, [brandingInfo]);

  useEffect(() => {
    if (verificationInfo.Success) {
      vp.setTitle(verificationInfo.PageTitle ?? 'VoPay Verify');
      vp.setFavicon(verificationInfo.Favicon ?? '/src/assets/images/favicon/vopay-favicon.svg');
      fetchAccountVerification({
        VerificationRequestID: verificationInfo.ExtraInfo.VerificationDetails?.VerificationRequestID || '',
        Token: verificationInfo.Token || '',
      });
      fetchAccountBranding({
        Token: accountVerificationToken,
        Product: vp.BrandingProductType.Elinx,
      });

      storage.JWT.storeJWT(verificationInfo.JWT);
    } else if (verificationInfo.ErrorMessage.includes('The provided Token is expired')) {
      fetchAccountBranding({
        Token: accountVerificationToken,
        Product: vp.BrandingProductType.Elinx,
      });
      history.push('/connect/verification/expired');
    }
  }, [verificationInfo]);

  function verificationRequestsReset() {
    fetchAccountVerification({
      VerificationRequestID: verificationInfo.ExtraInfo.VerificationDetails?.VerificationRequestID || '',
      Token: verificationInfo.Token || '',
    });
  }

  return (
    <AccountVerificationContext.Provider
      value={{
        isLoaded,
        isVerification,
        verificationInfo,
        verificationRequests,
        brandingInfo: {
          ...brandingInfo,
          Logo: brandingInfo?.Logo,
        },
        verificationRequestsReset,
      }}
    >
      {children}
    </AccountVerificationContext.Provider>
  );
};

export const useAccountVerification = (): AccountVerificationCtx => useContext(AccountVerificationContext);
